import "./PublicDevelopmentManagerDetails.css";

import { Dispatch, SetStateAction } from "react";
import { useSearchParams } from "react-router-dom";

import { ArrowFullLeftDarkIcon } from "../../../../assets";
import { OrganisationTypeConstants } from "../../../../constants";
import { OrganisationType } from "../../../../models";
import { MapProjectCoordinates } from "../../../../models/shared/mapProjectCoordinates";
import { ProjectsSearch } from "../../../../route/public/projects/components";
import { getHostFromURL } from "../../../../utils";
import { Button, Column, Logo, Map, Pill, Row } from "../../../../widget";
import {
  AboutUsSection,
  AtAGlanceSection,
  ContactUsModal,
  ModalButton,
  RichContentSection,
  SocialMediaSection,
} from "../../../components";
import { DevelopmentManagerDetailsData } from "../../models";

interface PublicDevelopmentManagerDetailsProps {
  developmentManagerDetailsData?: DevelopmentManagerDetailsData;
  projectsCoordinates: MapProjectCoordinates[] | undefined;
  developmentManagerUuid: string | undefined | null;
  isPreview: boolean;
  showContactUsModal?: boolean;
  setShowContactUsModal?: Dispatch<SetStateAction<boolean>>;
  navigateBackToProject?: () => void;
}

export const PublicDevelopmentManagerDetails = ({
  developmentManagerDetailsData,
  projectsCoordinates,
  developmentManagerUuid,
  isPreview,
  showContactUsModal,
  setShowContactUsModal,
  navigateBackToProject,
}: PublicDevelopmentManagerDetailsProps): JSX.Element => {
  const [searchParams] = useSearchParams();
  const projectUuid = searchParams.get("projectUuid");
  const showMap = projectsCoordinates?.length;

  return (
    <div className="PublicDevelopmentManagerDetails">
      {developmentManagerDetailsData?.hero?.enabled && (
        <div
          className="HeroSectionOrganisationImage"
          style={{
            backgroundColor: `var(--color-indigo-25)`,
            backgroundImage:
              developmentManagerDetailsData?.hero?.imageUrl !== undefined
                ? `url(${developmentManagerDetailsData?.hero?.imageUrl})`
                : undefined,
          }}
        />
      )}
      <div className="PublicDevelopmentManagerDetailsContent">
        {developmentManagerDetailsData?.hero?.enabled ? (
          <>
            {developmentManagerDetailsData?.logo && (
              <div className="HeroSectionOrganisationLogo">
                <Logo displayedLogo={developmentManagerDetailsData?.logo} />
              </div>
            )}
            {!isPreview && setShowContactUsModal && (
              <div className="PreviewButtonsContainer">
                <div className={projectUuid ? "" : "HideBackToProjectButton"}>
                  <Button
                    text="Back to project"
                    variant="secondary"
                    icon={<ArrowFullLeftDarkIcon />}
                    iconPosition="left"
                    onClick={navigateBackToProject}
                  />
                </div>
                {developmentManagerDetailsData.calculatedContactable && (
                  <ModalButton
                    organisationType={OrganisationType.DevelopmentManager}
                    onClick={() => setShowContactUsModal(true)}
                  />
                )}
              </div>
            )}
            <Row spacingV="ml" justify="center">
              <Column span={12}>
                <h1>{developmentManagerDetailsData?.displayName}</h1>
              </Column>
            </Row>
            <Row spacingV="m" justify="center">
              <Column span={10}>
                <h2>{developmentManagerDetailsData?.hero?.title}</h2>
              </Column>
            </Row>
            <Row spacingV="ml" justify="center">
              <Column span={12}>
                <span className="body1">{developmentManagerDetailsData?.hero?.subtitle}</span>
              </Column>
            </Row>
            {developmentManagerDetailsData?.websiteUrl && (
              <Row spacingV="ml" justify="center">
                <Column span={12}>
                  <Pill
                    variant="blue"
                    label={getHostFromURL(developmentManagerDetailsData?.websiteUrl)}
                    link={developmentManagerDetailsData?.websiteUrl}
                  />
                </Column>
              </Row>
            )}
            <SocialMediaSection isPreview={isPreview} socialMedia={developmentManagerDetailsData.socialMediaData} />
          </>
        ) : (
          <>
            {!isPreview && setShowContactUsModal && (
              <div
                className={` ${
                  developmentManagerDetailsData?.logo
                    ? "NoHeroSectionPreviewButtonsContainer"
                    : "NoHeroSectionNoDeveloperLogoPreviewButtonsContainer"
                }`}
              >
                <div className={projectUuid ? "" : "HideBackToProjectButton"}>
                  <Button
                    text="Back to project"
                    variant="secondary"
                    icon={<ArrowFullLeftDarkIcon />}
                    iconPosition="left"
                    onClick={navigateBackToProject}
                  />
                </div>
                {developmentManagerDetailsData?.calculatedContactable && (
                  <ModalButton
                    organisationType={OrganisationType.DevelopmentManager}
                    onClick={() => setShowContactUsModal(true)}
                  />
                )}
              </div>
            )}
            {developmentManagerDetailsData?.logo && (
              <div className="NoHeroSectionOrganisationLogo">
                <Logo displayedLogo={developmentManagerDetailsData?.logo} />
              </div>
            )}
            <Row
              className={developmentManagerDetailsData?.logo ? "" : "NoHeroSectionNoOrganisationLogoDisplayName"}
              spacingV="ml"
              justify="center"
            >
              <Column span={12}>
                <h1>{developmentManagerDetailsData?.displayName}</h1>
              </Column>
            </Row>
            {developmentManagerDetailsData?.websiteUrl && (
              <Row spacingV="ml" justify="center">
                <Column span={12}>
                  <Pill
                    variant="blue"
                    label={getHostFromURL(developmentManagerDetailsData?.websiteUrl)}
                    link={developmentManagerDetailsData?.websiteUrl}
                  />
                </Column>
              </Row>
            )}
            {developmentManagerDetailsData?.socialMediaData && (
              <SocialMediaSection isPreview={isPreview} socialMedia={developmentManagerDetailsData.socialMediaData} />
            )}
          </>
        )}
        {showMap && projectsCoordinates && (
          <Row spacingV="ml">
            <Column span={12}>
              <Map fullWidth coordinates={projectsCoordinates} height={515} />
            </Column>
          </Row>
        )}
        {developmentManagerDetailsData?.atAGlance?.enabled && (
          <Row spacingV="ml">
            <Column span={12}>
              <AtAGlanceSection
                organisationUuid={developmentManagerUuid}
                organisationType={OrganisationTypeConstants.DEVELOPMENT_MANAGER}
              />
            </Column>
          </Row>
        )}
        {developmentManagerDetailsData?.ourServices?.enabled && (
          <Row spacingV="ml">
            <Column span={12}>
              <RichContentSection title="Our services" sectionData={developmentManagerDetailsData.ourServices} />
            </Column>
          </Row>
        )}
        <div className="OurProjectsSection">
          <Row spacingV="ll">
            <Column span={12}>
              <h2>Our projects</h2>
            </Column>
          </Row>
          <Row spacingV="2xl">
            <Column span={12}>
              <ProjectsSearch developerUuid={developmentManagerUuid} />
            </Column>
          </Row>
        </div>
        {developmentManagerDetailsData?.aboutUs?.enabled && developmentManagerDetailsData?.aboutUs?.content && (
          <Row>
            <Column span={12}>
              {developmentManagerDetailsData.aboutUs.imageUrl ? (
                <AboutUsSection
                  content={developmentManagerDetailsData.aboutUs.content}
                  imageUrl={developmentManagerDetailsData.aboutUs.imageUrl}
                />
              ) : (
                <div className="AboutUsSection">
                  <h2>About us</h2>
                  <p className="body1">{developmentManagerDetailsData.aboutUs.content}</p>
                </div>
              )}
            </Column>
          </Row>
        )}
      </div>
      {developmentManagerDetailsData?.displayName && setShowContactUsModal && showContactUsModal && (
        <ContactUsModal
          organisationType={OrganisationTypeConstants.DEVELOPMENT_MANAGER}
          developerName={developmentManagerDetailsData?.displayName}
          developerPhoneNumber={developmentManagerDetailsData?.contactPhone}
          show={showContactUsModal}
          closeModal={() => {
            setShowContactUsModal(false);
          }}
        />
      )}
    </div>
  );
};
